<template><markdown-wrapper><h1 id="messagebox">MessageBox <a class="header-anchor" href="#messagebox">&#128279;</a></h1>
<blockquote>
<p>A set of modal boxes simulating system message box, mainly for alerting information, confirm operations and prompting messages.</p>
</blockquote>
<p>By design MessageBox provides simulations of browsers' <code v-pre>alert</code>, <code v-pre>confirm</code> and <code v-pre>prompt</code>. Use Modal instead if needed more complicated contents.</p>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="alert">Alert <a class="header-anchor" href="#alert">&#128279;</a></h2>
<p>Displays an alert modal with the optional specified content and an OK (auto-focused) button. By default it can not be closed on backdrop click.</p>
<p>An alert example using callback:</p>
<message-box-alert ref="message-box-alert"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;alert&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Click to open an alert modal<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">alert</span> () {
        <span class="hljs-variable language_">this</span>.$alert({
          <span class="hljs-attr">title</span>: <span class="hljs-string">&#x27;Title&#x27;</span>,
          <span class="hljs-attr">content</span>: <span class="hljs-string">&#x27;This is an alert message.&#x27;</span>
        }, <span class="hljs-function">(<span class="hljs-params">msg</span>) =&gt;</span> {
          <span class="hljs-comment">// callback after modal dismissed</span>
          <span class="hljs-variable language_">this</span>.$notify(<span class="hljs-string">`You selected <span class="hljs-subst">${msg}</span>.`</span>)
        })
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- message-box-alert.vue --&gt;</span>
</code></pre>
<h2 id="confirm">Confirm <a class="header-anchor" href="#confirm">&#128279;</a></h2>
<p>Displays a modal dialog with an optional message and two buttons, OK (auto-focused) and Cancel.</p>
<p>A confirm example using <code v-pre>Promise</code>:</p>
<message-box-confirm ref="message-box-confirm"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;confirm&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Click to open a confirm modal<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">confirm</span> () {
        <span class="hljs-variable language_">this</span>.$confirm({
          <span class="hljs-attr">title</span>: <span class="hljs-string">&#x27;Confirm&#x27;</span>,
          <span class="hljs-attr">content</span>: <span class="hljs-string">&#x27;This item will be permanently deleted. Continue?&#x27;</span>
        })
          .<span class="hljs-title function_">then</span>(<span class="hljs-function">() =&gt;</span> {
            <span class="hljs-variable language_">this</span>.$notify({
              <span class="hljs-attr">type</span>: <span class="hljs-string">&#x27;success&#x27;</span>,
              <span class="hljs-attr">content</span>: <span class="hljs-string">&#x27;Delete completed.&#x27;</span>
            })
          })
          .<span class="hljs-title function_">catch</span>(<span class="hljs-function">() =&gt;</span> {
            <span class="hljs-variable language_">this</span>.$notify(<span class="hljs-string">&#x27;Delete canceled.&#x27;</span>)
          })
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- message-box-confirm.vue --&gt;</span>
</code></pre>
<h2 id="prompt">Prompt <a class="header-anchor" href="#prompt">&#128279;</a></h2>
<p>Displays a dialog with an optional message prompting the user to input some text.</p>
<message-box-prompt ref="message-box-prompt"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;confirm&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Click to open a prompt modal<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">confirm</span> () {
        <span class="hljs-variable language_">this</span>.$prompt({
          <span class="hljs-attr">title</span>: <span class="hljs-string">&#x27;Welcome&#x27;</span>,
          <span class="hljs-attr">content</span>: <span class="hljs-string">&#x27;Please input your email:&#x27;</span>,
          <span class="hljs-comment">// A simple input validator</span>
          <span class="hljs-comment">// returns the err msg (not valid) or null (valid)</span>
          <span class="hljs-title function_">validator</span> (value) {
            <span class="hljs-keyword">return</span> <span class="hljs-regexp">/\S+@\S+\.\S+/</span>.<span class="hljs-title function_">test</span>(value) ? <span class="hljs-literal">null</span> : <span class="hljs-string">&#x27;Email address is not valid!&#x27;</span>
          }
        })
          .<span class="hljs-title function_">then</span>(<span class="hljs-function">(<span class="hljs-params">value</span>) =&gt;</span> {
            <span class="hljs-variable language_">this</span>.$notify({
              <span class="hljs-attr">type</span>: <span class="hljs-string">&#x27;success&#x27;</span>,
              <span class="hljs-attr">content</span>: <span class="hljs-string">`You email address is <span class="hljs-subst">${value}</span>`</span>
            })
          })
          .<span class="hljs-title function_">catch</span>(<span class="hljs-function">() =&gt;</span> {
            <span class="hljs-variable language_">this</span>.$notify(<span class="hljs-string">&#x27;Input canceled.&#x27;</span>)
          })
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- message-box-prompt.vue --&gt;</span>
</code></pre>
<h2 id="global-methods">Global methods <a class="header-anchor" href="#global-methods">&#128279;</a></h2>
<p>Following global methods for <code v-pre>Vue.prototype</code> will be added <strong>if uiv is installed</strong>:</p>
<ul>
<li><code v-pre>$alert(options, callback(msg))</code></li>
<li><code v-pre>$confirm(options, callback(err, msg))</code></li>
<li><code v-pre>$prompt(options, callback(err, msg))</code></li>
</ul>
<p>Callback params:</p>
<ul>
<li><code v-pre>err</code> as user dismiss or cancel the box, otherwise it will be <code v-pre>null</code>. Note that there is no err in <code v-pre>$alert</code> callback.</li>
<li><code v-pre>msg</code> as the user input while using prompt.</li>
</ul>
<p>Each of these methods will return a <code v-pre>Promise</code> object that resolve / reject while the box is closed (if supported by browser or with es6 promise polyfill).</p>
<h2 id="import-individually">Import individually <a class="header-anchor" href="#import-individually">&#128279;</a></h2>
<p>If you prefer importing <code v-pre>MessageBox</code> individually:</p>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-keyword">import</span> { <span class="hljs-title class_">MessageBox</span> } <span class="hljs-keyword">from</span> <span class="hljs-string">&#x27;uiv&#x27;</span>
</code></pre>
<p>The corresponding methods are: <code v-pre>MessageBox.alert</code>, <code v-pre>MessageBox.confirm</code> and <code v-pre>MessageBox.prompt</code>, with same parameters as above.</p>
<p><strong>Note:</strong> You CAN NOT use MessageBox individually like this if you're also using <code v-pre>vue-i18n</code> in the root instance of Vue (because it will not be able to access the correct language pack), a workaround is to register them manually:</p>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-keyword">import</span> <span class="hljs-title class_">Vue</span> <span class="hljs-keyword">from</span> <span class="hljs-string">&#x27;vue&#x27;</span>
<span class="hljs-keyword">import</span> { <span class="hljs-title class_">MessageBox</span> } <span class="hljs-keyword">from</span> <span class="hljs-string">&#x27;uiv&#x27;</span>

<span class="hljs-title class_">Vue</span>.<span class="hljs-property"><span class="hljs-keyword">prototype</span></span>.<span class="hljs-property">$alert</span> = <span class="hljs-title class_">MessageBox</span>.<span class="hljs-property">alert</span>
<span class="hljs-comment">// ...</span>
</code></pre>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="messagebox-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/services/messagebox/MessageBox.vue">MessageBox</a> <a class="header-anchor" href="#messagebox-1">&#128279;</a></h2>
<p>These props are used as <code v-pre>options</code> in the methods above.</p>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>size</code></td>
<td>String</td>
<td>sm</td>
<td></td>
<td>The alternative modal sizes. Support: <code v-pre>lg</code> / <code v-pre>md</code> / <code v-pre>sm</code>.</td>
</tr>
<tr>
<td><code v-pre>title</code></td>
<td>String</td>
<td></td>
<td></td>
<td>The modal title.</td>
</tr>
<tr>
<td><code v-pre>content</code></td>
<td>String</td>
<td></td>
<td></td>
<td>The modal content.</td>
</tr>
<tr>
<td><code v-pre>html</code></td>
<td>Boolean</td>
<td><code v-pre>false</code></td>
<td></td>
<td>Allow HTML in content.</td>
</tr>
<tr>
<td><code v-pre>okText</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Text of ok button.</td>
</tr>
<tr>
<td><code v-pre>okType</code></td>
<td>String</td>
<td>primary</td>
<td></td>
<td>Button type of ok button.</td>
</tr>
<tr>
<td><code v-pre>cancelText</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Text of cancel button.</td>
</tr>
<tr>
<td><code v-pre>cancelType</code></td>
<td>String</td>
<td>default</td>
<td></td>
<td>Button type of cancel button.</td>
</tr>
<tr>
<td><code v-pre>customClass</code></td>
<td></td>
<td></td>
<td></td>
<td>Custom classes to modal, anything that can work with <code v-pre>v-bind:class</code> or <code v-pre>:class</code>.</td>
</tr>
<tr>
<td><code v-pre>backdrop</code></td>
<td>Boolean</td>
<td><code v-pre>false</code> if type is alert, otherwise <code v-pre>true</code></td>
<td></td>
<td>Dismiss the modal by backdrop click.</td>
</tr>
<tr>
<td><code v-pre>validator</code></td>
<td>Function</td>
<td></td>
<td></td>
<td>Custom validator function for prompt. Accepts the input value as param, returns the err msg (not valid) or null (valid)</td>
</tr>
<tr>
<td><code v-pre>defaultValue</code></td>
<td>String</td>
<td></td>
<td></td>
<td>(0.27.0+) The default value to fill in prompt box.</td>
</tr>
<tr>
<td><code v-pre>inputType</code></td>
<td>String</td>
<td>text</td>
<td></td>
<td>(0.28.0+) The default input type of prompt box.</td>
</tr>
<tr>
<td><code v-pre>autoFocus</code></td>
<td>String</td>
<td>ok</td>
<td></td>
<td>(0.31+) Focus on button while message box open. Can be <code v-pre>ok</code> / <code v-pre>cancel</code>, or empty String to disable the feature.</td>
</tr>
<tr>
<td><code v-pre>reverseButtons</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>(0.36+) Reverse the order of <code v-pre>ok</code> and <code v-pre>cancel</code> button.</td>
</tr>
</tbody>
</table></div></markdown-wrapper></template><script>
  

  

  
export default {components:{'message-box-alert':{"template":"<div class=\"markdown-live-example\">\n  <btn @click=\"alert\" type=\"primary\">Click to open an alert modal</btn>\n</div>",
    methods: {
      alert () {
        this.$alert({
          title: 'Title',
          content: 'This is an alert message.'
        }, (msg) => {
          // callback after modal dismissed
          this.$notify(`You selected ${msg}.`)
        })
      }
    }
  },'message-box-confirm':{"template":"<div class=\"markdown-live-example\">\n  <btn @click=\"confirm\" type=\"primary\">Click to open a confirm modal</btn>\n</div>",
    methods: {
      confirm () {
        this.$confirm({
          title: 'Confirm',
          content: 'This item will be permanently deleted. Continue?'
        })
          .then(() => {
            this.$notify({
              type: 'success',
              content: 'Delete completed.'
            })
          })
          .catch(() => {
            this.$notify('Delete canceled.')
          })
      }
    }
  },'message-box-prompt':{"template":"<div class=\"markdown-live-example\">\n  <btn @click=\"confirm\" type=\"primary\">Click to open a prompt modal</btn>\n</div>",
    methods: {
      confirm () {
        this.$prompt({
          title: 'Welcome',
          content: 'Please input your email:',
          // A simple input validator
          // returns the err msg (not valid) or null (valid)
          validator (value) {
            return /\S+@\S+\.\S+/.test(value) ? null : 'Email address is not valid!'
          }
        })
          .then((value) => {
            this.$notify({
              type: 'success',
              content: `You email address is ${value}`
            })
          })
          .catch(() => {
            this.$notify('Input canceled.')
          })
      }
    }
  }}}</script>